import { notification } from "antd";
import api from "ApiClient";
import { ApiURL } from "utils/Actions";
import { DocumentUploadType } from "utils/Types";
import { FILE_RESTRICTION } from "utils/Constants";
import uniqueId from "lodash/uniqueId";

export const uploadDocuments = (formData: any): Promise<DocumentUploadType> => {
  const url = `${ApiURL.uploadDocument}`;

  return api
    .post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
    .then(response => {
      if (response.data.success) {
        return {
          error: false,
          docs: response.data.docs
        };
      } else {
        return {
          error: true,
          errorInfo: response.data.errorInfo
        };
      }
    })
    .catch(error => {
      let errorInfo = "";
      if (error.response.status === 422) {
        error.response.data["errors"].forEach((err: any) => {
          errorInfo = err.message;
        });
      }
      return {
        error: true,
        errorInfo: errorInfo
      };
    });
};

export const downloadBlobResponse = (response: any) => {
  const fileName = response.headers["content-disposition"]
    ?.split("filename=")[1]
    .replaceAll('"', "");
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", fileName ? fileName : "downloaded-file.pdf");
  document.body.appendChild(link);
  link.click();
};

export const getDocument = async (refNum: string, fileReference: string) => {
  const url = `${ApiURL.getEasDoc}/${refNum}/${fileReference}`;
  try {
    const res = await api.get(url, { responseType: "blob" });
    downloadBlobResponse(res);
  } catch (error) {
    notification["error"]({
      message: "Cannot download file"
    });
    return false;
  }
};

export const displayFile = (file: any) => {
  const fileData = new Blob([file], { type: "application/pdf" });
  const fileURL = URL.createObjectURL(fileData);
  const pdfWindow = window.open();
  if (pdfWindow) {
    pdfWindow.location.href = fileURL;
  } else {
    notification["error"]({
      message: "Cannot display file"
    });
  }
};

export const validateFile = (file: any) => {
  const englishCharPattern = /^[\u0020-\u0080]+$/;
  let valid = true;
  const errors = [];

  // File name length has to be less than 50
  if (file.name.length > FILE_RESTRICTION.NAME_LENGTH) {
    valid = false;
    const id = uniqueId("nameError-");
    const msg = (
      <div>
        {`The file name exceeds the ${FILE_RESTRICTION.NAME_LENGTH}-character limit. Please shorten the name and try again.`}
      </div>
    );
    errors.push({ id, msg });
  }

  // File name has to use ASCII
  if (!file.name.match(englishCharPattern)) {
    valid = false;
    const id = uniqueId("charError-");
    const msg = (
      <div>
        <strong>{file.name}</strong> is not valid name.
      </div>
    );
    errors.push({ id, msg });
  }

  // Individual file size has to be less than 5MB
  if (file.size > FILE_RESTRICTION.SIZE) {
    valid = false;
    const id = uniqueId("sizeError-");
    const msg = (
      <div>
        <strong>{file.name}</strong> is too big to upload. The maximum file size is 5MB.
      </div>
    );
    errors.push({ id, msg });
  }

  // File type has to be PDF
  if (file.type !== FILE_RESTRICTION.TYPE) {
    valid = false;
    const id = uniqueId("typeError-");
    const msg = (
      <div>
        <strong>{file.name}</strong>
        {` failed to upload. We only accept PDF files.`}
      </div>
    );
    errors.push({ id, msg });
  }
  return { valid, errors };
};
